import { reactive } from 'vue'
import { apiRequest } from '../services/apiClient'
import { getValidToken, getCurrentUser } from '../services/authService'
import { subscribeToUserChannel, removeSubscription, onClientReset } from '../services/centrifugoService'
import { t } from '../services/i18nService'

const SOUND_PREF_KEY = 'client_alarm_sound' // legacy-ключ (тільки on/off) — тримаємо в синхроні для відкату фронта
const SOUND_PREFS_KEY = 'client_alarm_sound_prefs'
const API_BASE = import.meta.env.VITE_CLIENT_API_BASE || '/client/api'

// Синглтон — module-level reactive state (аналог useOnlineStatus.js)
// Хранит set device ID с активной тревогой (на основе alarmTriggered из API)
const activeAlarms = reactive(new Set())
// deviceId → { reason, ts } останньої непідтвердженої тривоги (з /devices/alarm/check)
const alarmReasons = reactive(new Map())

// Предыдущий снимок для определения НОВЫХ тревог (для звука)
let previousAlarmIds = new Set()

// Polling для real-time тривог (module-level singleton)
let alarmPollTimer = null

// Мітка останнього успішного запиту — захист від залпового спрацювання після iOS freeze
let lastCheckTs = 0

// Callback для оновлення маркерів на карті після зміни стану тривог
let _markersRefreshHandler = null

// --- Налаштування звуку (localStorage, per-browser) ---

function loadSoundPrefs() {
  const defaults = {
    enabled: true,       // звуковий сигнал увімкнено
    melody: 'beep',      // beep | siren | chime | long
    volume: 0.6,         // 0..1
    repeatSec: 15,       // повтор поки тривогу не підтверджено; 0 = один раз
    systemNotify: false, // системні сповіщення браузера (Notification API)
  }
  try {
    const raw = localStorage.getItem(SOUND_PREFS_KEY)
    if (raw) return { ...defaults, ...JSON.parse(raw) }
  } catch { /* битий JSON — беремо дефолти */ }
  // Міграція зі старого ключа, де зберігався тільки on/off
  if (localStorage.getItem(SOUND_PREF_KEY) === 'false') defaults.enabled = false
  return defaults
}

// Reactive, щоб кнопка в alarm-барі та налаштування оновлювались синхронно
const soundPrefs = reactive(loadSoundPrefs())

function saveSoundPrefs() {
  try {
    localStorage.setItem(SOUND_PREFS_KEY, JSON.stringify({ ...soundPrefs }))
    localStorage.setItem(SOUND_PREF_KEY, String(soundPrefs.enabled))
  } catch { /* private mode — налаштування живуть до перезавантаження */ }
}

// --- Web Audio: генерація мелодій без зовнішніх файлів ---

let audioCtx = null
let audioUnlockBound = false

// Браузер тримає AudioContext у suspended до першого жесту користувача.
// Вішаємо постійний listener: розблоковує і після повторних suspend (iOS freeze/resume).
function ensureAudioUnlockListener() {
  if (audioUnlockBound || typeof window === 'undefined') return
  audioUnlockBound = true
  const unlock = () => {
    try {
      if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)()
      // resume() возвращает промис: на iOS он реджектится InvalidStateError, а синхронный
      // try/catch его не ловит — получаем unhandled rejection в консоли и в Sentry.
      if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {})
    } catch { /* Web Audio недоступний */ }
  }
  window.addEventListener('pointerdown', unlock, { passive: true })
  window.addEventListener('keydown', unlock)
}

function playAlarmSound(melodyOverride) {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)()
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {})
    const vol = Math.max(0, Math.min(1, Number(soundPrefs.volume) || 0))
    if (vol === 0) return
    const t0 = audioCtx.currentTime

    // Простий тон із загасанням
    const tone = (start, freq, duration, type = 'sine', peak = 0.5) => {
      const osc = audioCtx.createOscillator()
      const gain = audioCtx.createGain()
      osc.connect(gain)
      gain.connect(audioCtx.destination)
      osc.frequency.value = freq
      osc.type = type
      gain.gain.setValueAtTime(peak * vol, start)
      gain.gain.exponentialRampToValueAtTime(0.01, start + duration)
      osc.start(start)
      osc.stop(start + duration)
    }

    switch (melodyOverride || soundPrefs.melody) {
      case 'siren': {
        // Частотний свіп 600↔1200 Гц, два цикли
        const osc = audioCtx.createOscillator()
        const gain = audioCtx.createGain()
        osc.connect(gain)
        gain.connect(audioCtx.destination)
        osc.type = 'triangle'
        osc.frequency.setValueAtTime(600, t0)
        osc.frequency.linearRampToValueAtTime(1200, t0 + 0.3)
        osc.frequency.linearRampToValueAtTime(600, t0 + 0.6)
        osc.frequency.linearRampToValueAtTime(1200, t0 + 0.9)
        osc.frequency.linearRampToValueAtTime(600, t0 + 1.2)
        gain.gain.setValueAtTime(0.5 * vol, t0)
        gain.gain.setValueAtTime(0.5 * vol, t0 + 1.0)
        gain.gain.exponentialRampToValueAtTime(0.01, t0 + 1.25)
        osc.start(t0)
        osc.stop(t0 + 1.25)
        break
      }
      case 'chime': {
        // Дзвіночок: дві високі ноти з обертоном і довгим загасанням
        tone(t0, 1318.5, 0.7, 'sine', 0.45)        // E6
        tone(t0, 2637, 0.35, 'sine', 0.12)         // обертон E7
        tone(t0 + 0.35, 1046.5, 0.9, 'sine', 0.45) // C6
        tone(t0 + 0.35, 2093, 0.4, 'sine', 0.12)   // обертон C7
        break
      }
      case 'long': {
        // Довгий тон із пульсацією гучності
        const osc = audioCtx.createOscillator()
        const gain = audioCtx.createGain()
        osc.connect(gain)
        gain.connect(audioCtx.destination)
        osc.type = 'sine'
        osc.frequency.value = 740
        gain.gain.setValueAtTime(0.5 * vol, t0)
        gain.gain.linearRampToValueAtTime(0.2 * vol, t0 + 0.3)
        gain.gain.linearRampToValueAtTime(0.5 * vol, t0 + 0.6)
        gain.gain.linearRampToValueAtTime(0.2 * vol, t0 + 0.9)
        gain.gain.exponentialRampToValueAtTime(0.01, t0 + 1.4)
        osc.start(t0)
        osc.stop(t0 + 1.4)
        break
      }
      default: {
        // beep — короткий двотональний сигнал (як було)
        tone(t0, 880, 0.15)
        tone(t0 + 0.2, 1100, 0.15)
      }
    }
  } catch { /* Браузер заблокував автовідтворення — пропускаємо */ }
}

// --- Повтор сигналу, поки тривогу не підтверджено ---

let repeatTimer = null
let lastSoundTs = 0

function syncRepeatTimer() {
  const need = activeAlarms.size > 0 && soundPrefs.enabled && soundPrefs.repeatSec > 0
  if (need && !repeatTimer) {
    // Тікаємо щосекунди і порівнюємо з lastSoundTs: у фоновій вкладці браузер
    // троттлить інтервали до ~1/хв — повтор просто стане рідшим, без залпу.
    repeatTimer = setInterval(() => {
      if (!(activeAlarms.size > 0 && soundPrefs.enabled && soundPrefs.repeatSec > 0)) {
        syncRepeatTimer()
        return
      }
      if (Date.now() - lastSoundTs >= soundPrefs.repeatSec * 1000 - 250) {
        lastSoundTs = Date.now()
        playAlarmSound()
      }
    }, 1000)
  } else if (!need && repeatTimer) {
    clearInterval(repeatTimer)
    repeatTimer = null
  }
}

// --- Системні сповіщення браузера (Notification API) ---

function systemNotifyGranted() {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted'
}

async function notifySystemAlarm() {
  if (!soundPrefs.systemNotify || !systemNotifyGranted()) return
  // В активній вкладці користувач і так бачить червоний банер і чує звук
  if (!document.hidden && document.hasFocus()) return
  const title = t('alarm.banner')
  const options = {
    body: t('alarm.globalBar', { count: activeAlarms.size }),
    tag: 'cloudgps-alarm', // нова тривога замінює попереднє сповіщення, а не плодить стек
    renotify: true,
    icon: '/icons/icon-192.png',
  }
  try {
    // Chrome Android дозволяє сповіщення тільки через service worker
    const reg = await navigator.serviceWorker?.getRegistration()
    if (reg?.showNotification) {
      reg.showNotification(title, options)
      return
    }
  } catch { /* впадемо на конструктор нижче */ }
  try {
    const n = new Notification(title, options)
    n.onclick = () => { try { window.focus(); n.close() } catch { /* ignore */ } }
  } catch { /* Notification недоступний */ }
}

// Єдина точка сигналізації про нові тривоги: звук + системне сповіщення
function triggerAlarmSignal() {
  lastSoundTs = Date.now()
  if (soundPrefs.enabled) playAlarmSound()
  notifySystemAlarm()
}

// --- Centrifugo WS: канал user:{ownerUserId} як poke-сигнал ---
// Подія з каналу не застосовується до стану напряму: фронт одразу дёргает alarm/check,
// тож сервер лишається єдиним джерелом правди (ack-стан, володіння). WS лише прибирає
// 2с-затримку і дозволяє розрідити поллінг до 30с (розвантаження API, ліміт 600/хв).

let alarmWsSub = null
let alarmWsUserId = 0
let wsPokeTimer = null

// Після logout centrifugoService дропає клієнт цілком — занулюємо мертву підписку,
// наступний тик поллінгу пересоздасть її вже для нового користувача
onClientReset(() => {
  if (wsPokeTimer) { clearTimeout(wsPokeTimer); wsPokeTimer = null }
  alarmWsSub = null
  alarmWsUserId = 0
})

function wsAlive() {
  return !!alarmWsSub && alarmWsSub.state === 'subscribed'
}

// Коалесуємо WS-poke: залп подій (кілька тривог одразу) → один fetch
function pokeCheck() {
  if (wsPokeTimer) return
  wsPokeTimer = setTimeout(() => {
    wsPokeTimer = null
    checkAlarms(true)
  }, 250)
}

function ensureAlarmWs() {
  const u = getCurrentUser()
  // Канал завжди на власника акаунта: для суб-користувача це parentUserId
  const ownerId = Number(u?.isSubUser ? u?.parentUserId : u?.id) || 0
  if (!ownerId || !getValidToken()) return
  if (alarmWsSub && alarmWsUserId === ownerId) return
  cleanupAlarmWs()
  try {
    alarmWsSub = subscribeToUserChannel(ownerId, {
      // Будь-яка подія каналу (alarm_trigger від telemetry_geozones, alarm_ack-эхо) → негайна звірка.
      // Канал спільний: support_message — це poke бейджа підтримки (useSupportUnread), не тривога
      onPublication: (ctx) => {
        if (ctx?.data?.type === 'support_message') {
          window.dispatchEvent(new Event('support-unread-poke'))
          return
        }
        // «Охорона»: постановка/зняття з іншої вкладки або телефону — сторінка «Транспорт»
        // оновлює щит і коло без перезапиту списку; тривоги тут ні до чого
        if (ctx?.data?.type === 'guard_state') {
          window.dispatchEvent(new CustomEvent('guard-state-changed', { detail: ctx.data }))
          return
        }
        pokeCheck()
      },
      // (Ре)підключення після розриву: звіряємось одразу — могли пропустити тривогу
      onSubscribed: () => pokeCheck(),
    })
    alarmWsUserId = ownerId
  } catch {
    // Centrifugo недоступний — лишаємось на 2с-поллінгу
    alarmWsSub = null
    alarmWsUserId = 0
  }
}

function cleanupAlarmWs() {
  if (wsPokeTimer) { clearTimeout(wsPokeTimer); wsPokeTimer = null }
  if (alarmWsSub) {
    removeSubscription(alarmWsSub)
    alarmWsSub = null
  }
  alarmWsUserId = 0
}

// --- Lightweight alarm polling (замість SSE — не блокує PHP-воркер) ---

// Інтервал між реальними fetch'ами: з живим WS достатньо рідкої звірки,
// без WS — колишні 2с. WS-poke (force) проходить поза розкладом.
const POLL_GAP_MS = 2000
const POLL_GAP_WS_MS = 30000

async function checkAlarms(force = false) {
  try {
    // Захист від залпу: iOS Safari після freeze/resume може запустити декілька
    // накопичених callbacks одночасно; force-виклики (WS poke) теж коалесуємо
    const now = Date.now()
    const minGap = force ? 500 : (wsAlive() ? POLL_GAP_WS_MS : POLL_GAP_MS) - 250
    if (now - lastCheckTs < minGap) return
    lastCheckTs = now

    const token = getValidToken()
    if (!token) return

    // На прихованій вкладці запит має сенс лише коли є кому сигналити:
    // звук (Web Audio працює у фоні після unlock) або системні сповіщення.
    if (document.hidden && !soundPrefs.enabled && !(soundPrefs.systemNotify && systemNotifyGranted())) return

    const resp = await fetch(`${API_BASE}/devices/alarm/check`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!resp.ok) return

    const data = await resp.json()
    const incomingIds = new Set((data.deviceIds || []).map(Number))
    const newAlarmIds = []

    // Причина останньої тривоги (охорона: guard_zone_exit / guard_ignition / …), щоб попап
    // показав «що сталося», а не лише «ТРИВОГА»
    alarmReasons.clear()
    for (const item of Array.isArray(data.alarms) ? data.alarms : []) {
      const id = Number(item?.deviceId)
      if (Number.isFinite(id)) alarmReasons.set(id, { reason: item.reason || null, ts: item.ts || null })
    }

    // Визначаємо НОВІ тривоги (яких не було раніше — для звуку)
    for (const id of incomingIds) {
      if (!previousAlarmIds.has(id)) {
        newAlarmIds.push(id)
      }
    }

    // Перевіряємо чи стан змінився (щоб не оновлювати маркери даремно)
    const stateChanged = incomingIds.size !== activeAlarms.size ||
      [...incomingIds].some(id => !activeAlarms.has(id))

    // Оновлюємо reactive state
    activeAlarms.clear()
    for (const id of incomingIds) {
      activeAlarms.add(id)
    }
    previousAlarmIds = incomingIds

    // Сповіщаємо про нові тривоги (звук + системне сповіщення)
    if (newAlarmIds.length > 0) {
      triggerAlarmSignal()
    }
    syncRepeatTimer()

    // Оновлюємо маркери якщо стан змінився
    if (stateChanged && _markersRefreshHandler) {
      _markersRefreshHandler()
    }
  } catch { /* Network error — пропускаємо, спробуємо через 2с */ }
}

function startAlarmPolling() {
  ensureAudioUnlockListener()
  if (alarmPollTimer) return
  ensureAlarmWs()
  checkAlarms() // Перша перевірка одразу
  // Тик кожні 2с: ensureAlarmWs — дешевий no-op поки підписка жива, а сам fetch
  // виконається раз на 2с (без WS) або раз на 30с (WS живий) — див. minGap у checkAlarms
  alarmPollTimer = setInterval(() => {
    ensureAlarmWs()
    checkAlarms()
  }, 2000)
}

function stopAlarmPolling() {
  cleanupAlarmWs()
  if (alarmPollTimer) {
    clearInterval(alarmPollTimer)
    alarmPollTimer = null
  }
}

/**
 * Composable для управления состоянием тревог устройств.
 * Источник правды — бекенд (event table + device_alarm_ack).
 * Polling запускається глобально з App.vue — тривоги (звук/сповіщення)
 * працюють на всіх сторінках портала, а не тільки на «Транспорт».
 */
export function useAlarmState() {

  /**
   * Обрабатывает список устройств после telemetry polling (fallback).
   * Коли alarm polling активне — повертає порожній масив (вже оновлює стан окремо).
   */
  function processDevices(devices) {
    // Якщо alarm polling активне — воно вже оновлює activeAlarms окремим запитом
    if (alarmPollTimer) return []

    const newAlarmIds = []
    const currentAlarms = new Set()

    for (const device of devices) {
      if (device.alarmTriggered === true) {
        currentAlarms.add(device.id)
        if (!previousAlarmIds.has(device.id)) {
          newAlarmIds.push(device.id)
        }
      }
    }

    activeAlarms.clear()
    for (const id of currentAlarms) {
      activeAlarms.add(id)
    }
    previousAlarmIds = currentAlarms

    // Сигналізуємо і у fallback-шляху — щоб звук не залежав від того, хто оновив стан
    if (newAlarmIds.length > 0) {
      triggerAlarmSignal()
    }
    syncRepeatTimer()

    return newAlarmIds
  }

  /** Подтвердить тревогу одного устройства (через API) */
  async function acknowledgeAlarm(deviceId) {
    // Оптимистичне оновлення — прибираємо з UI одразу
    activeAlarms.delete(deviceId)
    previousAlarmIds.delete(deviceId)
    syncRepeatTimer()

    // Оновлюємо маркери одразу після оптимістичного оновлення
    if (_markersRefreshHandler) _markersRefreshHandler()

    try {
      const token = getValidToken()
      await apiRequest('/devices/alarm/acknowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deviceId }),
      })
    } catch {
      // При помилці тривога повернеться через alarm polling
    }
  }

  /** Подтвердить все активные тревоги (через API) */
  async function acknowledgeAll() {
    activeAlarms.clear()
    previousAlarmIds.clear()
    syncRepeatTimer()

    // Оновлюємо маркери одразу після оптимістичного оновлення
    if (_markersRefreshHandler) _markersRefreshHandler()

    try {
      const token = getValidToken()
      await apiRequest('/devices/alarm/acknowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ all: true }),
      })
    } catch {
      // При помилці тривоги повернуться через alarm polling
    }
  }

  /** Проверить, есть ли активная тревога на устройстве */
  function isAlarmActive(deviceId) {
    return activeAlarms.has(deviceId)
  }

  /** Причина активної тривоги (guard_* для охорони, null = звичайна тривога з пристрою) */
  function getAlarmReason(deviceId) {
    return alarmReasons.get(Number(deviceId))?.reason ?? null
  }

  /** Есть ли хотя бы одна активная тревога */
  function hasAnyActiveAlarm() {
    return activeAlarms.size > 0
  }

  /** Количество устройств с активной тревогой */
  function getActiveAlarmCount() {
    return activeAlarms.size
  }

  /** Включён ли звук тревоги (реактивно) */
  function isSoundEnabled() {
    return soundPrefs.enabled
  }

  /** Переключить звук тревоги */
  function toggleSound() {
    soundPrefs.enabled = !soundPrefs.enabled
    saveSoundPrefs()
    syncRepeatTimer()
  }

  /** Установить настройку звука: melody | volume | repeatSec | systemNotify | enabled */
  function setSoundPref(key, value) {
    soundPrefs[key] = value
    saveSoundPrefs()
    syncRepeatTimer()
  }

  /** Прослушать мелодию (кнопка в настройках; клик = user gesture, разблокирует аудио) */
  function previewSound(melody) {
    playAlarmSound(melody || soundPrefs.melody)
  }

  /**
   * Запросить разрешение на системные уведомления.
   * Возвращает 'granted' | 'denied' | 'default' | 'unsupported'.
   */
  async function requestSystemNotifyPermission() {
    if (typeof Notification === 'undefined') return 'unsupported'
    if (Notification.permission === 'granted') return 'granted'
    if (Notification.permission === 'denied') return 'denied'
    try {
      return await Notification.requestPermission()
    } catch {
      return 'denied'
    }
  }

  /** Встановити callback для оновлення маркерів на карті */
  function onMarkersRefresh(handler) {
    _markersRefreshHandler = handler
  }

  /** Запустити відстеження тривог: WS-канал user:{owner} + поллінг-фолбэк (2с без WS / 30с з WS) */
  function startPolling() {
    startAlarmPolling()
  }

  /** Зупинити alarm polling */
  function stopPolling() {
    stopAlarmPolling()
  }

  return {
    processDevices,
    acknowledgeAlarm,
    acknowledgeAll,
    isAlarmActive,
    getAlarmReason,
    hasAnyActiveAlarm,
    getActiveAlarmCount,
    soundPrefs,
    isSoundEnabled,
    toggleSound,
    setSoundPref,
    previewSound,
    requestSystemNotifyPermission,
    onMarkersRefresh,
    startPolling,
    stopPolling,
  }
}
